/**
 * Config management commands
 *
 * Manage user configs stored in backend KV that auto-sync to VMs
 */

import { readFile } from 'fs/promises';
import { requireAuth } from './auth.js';
import { loadConfig, saveConfig } from '../config.js';
import type { ConfigKey } from '../api/types.js';
import chalk from 'chalk';

/**
 * Set API URL in config
 */
export async function setApiUrl(apiUrl: string): Promise<void> {
  const config = await loadConfig();
  config.apiUrl = apiUrl;
  await saveConfig(config);
  console.log(chalk.green('✓ API URL saved to config'));
  console.log(chalk.gray(`  URL: ${apiUrl}`));
}

/**
 * Show current local config
 */
export async function showLocalConfig(): Promise<void> {
  const config = await loadConfig();

  console.log(chalk.cyan('📋 Local Configuration:\n'));

  const entries = Object.entries(config);
  if (entries.length === 0) {
    console.log(chalk.gray('No local configuration found.'));
    return;
  }

  for (const [key, value] of entries) {
    const displayKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
    const displayValue = String(value ?? '(not set)');
    console.log(`  ${chalk.green(displayKey.padEnd(15))} ${chalk.gray(displayValue)}`);
  }
}

/**
 * List all stored configs
 */
export async function listConfigs(): Promise<void> {
  const apiClient = await requireAuth();

  const response = await apiClient.getConfigs();

  console.log(chalk.cyan('📋 Stored Configs:\n'));

  const configEntries = Object.entries(response.config);
  if (configEntries.length === 0) {
    console.log(chalk.gray('No configs stored yet.'));
    console.log(chalk.gray('Use: spacecode config --set <key> < <file>'));
    return;
  }

  const configPaths: Record<ConfigKey, string> = {
    claudeMd: '~/.claude/CLAUDE.md',
    settingsJson: '~/.claude/settings.json',
    settingsLocal: '~/.claude/settings.local.json',
    prd: '~/workspace/PRD.md',
    progress: '~/workspace/docs/PROGRESS.md',
  };

  for (const [key, value] of configEntries) {
    if (value !== null) {
      const lines = value.split('\n').length;
      const path = configPaths[key as ConfigKey] ?? key;
      console.log(`  ${chalk.green(key.padEnd(15))} ${chalk.gray(`→ ${path}`)} ${chalk.gray(`(${lines} lines)`)}`);
    }
  }
}

/**
 * Get a specific config value
 */
export async function getConfig(key: ConfigKey): Promise<void> {
  const apiClient = await requireAuth();

  const response = await apiClient.getConfig(key);

  if (response.value === null) {
    console.log(chalk.yellow(`No config stored for '${key}'`));
    console.log(chalk.gray('Use: spacecode config --set <key> < <file>'));
    return;
  }

  console.log(response.value);
}

/**
 * Set a config value from a file
 */
export async function setConfig(key: ConfigKey, filePath: string): Promise<void> {
  const apiClient = await requireAuth();

  // Read content from stdin or file
  let content: string;
  if (filePath === '-') {
    // Read from stdin
    const chunks: Buffer[] = [];
    for await (const chunk of process.stdin) {
      chunks.push(chunk as Buffer);
    }
    content = Buffer.concat(chunks).toString('utf-8');
  } else {
    // Read from file
    try {
      content = await readFile(filePath, 'utf-8');
    } catch (error) {
      console.error(chalk.red(`✗ Failed to read file: ${filePath}`));
      throw error;
    }
  }

  const response = await apiClient.setConfig(key, content);

  console.log(chalk.green(`✓ Config '${response.key}' saved to backend`));
  console.log(chalk.gray(`  Sync path: ${response.path}`));
}

/**
 * Delete a config value
 */
export async function deleteConfig(key: ConfigKey): Promise<void> {
  const apiClient = await requireAuth();

  await apiClient.deleteConfig(key);

  console.log(chalk.green(`✓ Config '${key}' deleted`));
}

/**
 * Show config paths for reference
 */
export function showConfigPaths(): void {
  console.log(chalk.cyan('📋 Config Keys and Paths:\n'));
  console.log('  Key               Path on VM');
  console.log('  ' + chalk.gray('─'.repeat(60)));
  console.log('  claudeMd          ~/.claude/CLAUDE.md');
  console.log('  settingsJson      ~/.claude/settings.json');
  console.log('  settingsLocal     ~/.claude/settings.local.json');
  console.log('  prd               ~/workspace/PRD.md');
  console.log('  progress          ~/workspace/docs/PROGRESS.md');
  console.log();
  console.log(chalk.gray('Examples:'));
  console.log('  spacecode config --list');
  console.log('  spacecode config --get prd');
  console.log('  spacecode config --set prd < PRD.md');
  console.log('  spacecode config --delete prd');
}
